import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { SectionIDProp, SectionPropRelation, SectionPropSize } from './Section';
import { useLocalStorage } from '../../utilities/useLocalStorage';

type SectionCollapsedProp = boolean;

interface SectionContextData {
  id: SectionIDProp;
  collapsible: boolean;
  collapsed: SectionCollapsedProp;
  setCollapsed: (value: SectionCollapsedProp) => void;
  relation: SectionPropRelation;
  size: SectionPropSize;
}

const SectionContext = createContext<SectionContextData>({
  id: '',
  collapsible: false,
  collapsed: false,
  setCollapsed: () => {},
  relation: 'standalone',
  size: 'medium',
});

export function SectionProvider({
  children,
  collapsible,
  id,
  relation,
  size,
}: {
  collapsible?: boolean;
  children?: ReactNode;
  id?: SectionIDProp;
  relation: SectionPropRelation;
  size: SectionPropSize;
}) {
  const [collapsedState, setCollapsedState] = useLocalStorage<SectionCollapsedProp>(
    `atto.section.collapsed.${id}`,
    false,
  );

  const value = useMemo(
    () => ({
      collapsible: collapsible || false,
      collapsed: collapsedState,
      id: id || '',
      setCollapsed: setCollapsedState,
      relation: relation || 'standalone',
      size: size || 'medium',
    }),
    [collapsible, collapsedState, id, relation, size, setCollapsedState],
  );

  return <SectionContext.Provider value={value}>{children}</SectionContext.Provider>;
}

export function useSectionContext() {
  return useContext(SectionContext);
}
